import {CellRangeEventArgs, CellType, Column, FlexGrid} from "@mescius/wijmo.grid";
import {ListBox, MultiSelectListBox} from "@mescius/wijmo.input";
import {CollectionView, EventArgs, hidePopup, PopupPosition, showPopup, PropertyGroupDescription} from "@mescius/wijmo";
import {Button} from "@fluentui/web-components";

type RegisteredDomEventHandlers = Record<string, { handler: any, options?: any }>;

/**
 * A utility class for managing column properties, visibility, and widths in a FlexGrid.
 * The class includes functionality for persisting column widths in localStorage and providing
 * a UI for toggling column visibility through a column chooser menu.
 */
export class WjGridColumnChooser {

    constructor(private gridKey: string, private grid: FlexGrid) {
        grid.resizedColumn.addHandler(this.onColumnResized.bind(this));
        grid.draggedColumn.addHandler(this.onColumnDragged.bind(this));
        grid.columns.collectionChanged.addHandler(() => this.applyMemorizedColumnWidths());
        this.addColumnChooserMenu(grid);
        this.applyMemorizedColumnWidths();
    }

    private applyMemorizedColumnWidths() {
        const configObject = this.loadConfigObject();
        this.grid.columns.forEach((col, i) => {
            const key = this.getColumnKey(col);
            if (key && configObject[key]) {
                col.width = parseInt(configObject[key]);
            }
        });
    }

    public clearMemorizedColumnWidths() {
        for (let key in localStorage) {
            if (key.startsWith(`wjgrid.columns.${this.gridKey}`)) {
                localStorage.removeItem(key);
            }
        }
    }

    private onColumnResized(s: FlexGrid, e: CellRangeEventArgs) {
        const colWidth = this.grid.columns[e.col].width;
        this.memoizeColumnWidth(e.col, colWidth);
    }

    private loadConfigObject() {
        const configObj = localStorage.getItem(`wjgrid.columns.${this.gridKey}`);
        if (configObj) {
            return JSON.parse(configObj);
        } else {
            localStorage.setItem(`wjgrid.columns.${this.gridKey}`, '{}');
            return {};
        }
    }

    private storeConfigObject(configObj: any) {
        localStorage.setItem(`wjgrid.columns.${this.gridKey}`, JSON.stringify(configObj));
    }

    private getColumnKey(column: Column | number): string | undefined {
        if (typeof column === 'number') {
            column = this.grid.columns[column];
        }
        return column?.header?.toLowerCase() ?? undefined;
    }

    private memoizeColumnWidth(column: Column | number, width: number) {
        const configObj = this.loadConfigObject();
        const key = this.getColumnKey(column);
        if (key) {
            configObj[key] = width;
            this.storeConfigObject(configObj);
        }
    }

    private onColumnDragged(s: FlexGrid, e: CellRangeEventArgs) {
        console.log('Column dragged', e.col);
    }

    static addColumnChooser(gridKey: string, grid: FlexGrid): WjGridColumnChooser {
        let colSizer = new WjGridColumnChooser(gridKey, grid);
        return colSizer;
    }

    private addColumnChooserMenu(grid: FlexGrid) {
        const popupHost = document.createElement('div');
        grid.hostElement.parentElement!.append(popupHost);

        const colChooser = new ListBox(popupHost);
        colChooser.hostElement.classList.add('column-picker');

        class ColAdapter {
            header: string;
            groupName: string;

            constructor(public col: Column) {
                this.header = col.header ?? '';
                this.groupName = (col as any)['groupName'] ?? '';
            }

            get visible() {
                return this.col.visible;
            }
            set visible(v: boolean) {
                this.col.visible = v;
            }
        }

        const cv = new CollectionView(grid.columns.map(c => new ColAdapter(c)));
        cv.groupDescriptions.push(new PropertyGroupDescription('groupName'));

        colChooser.itemsSource = cv;
        colChooser.checkedMemberPath = 'visible';
        colChooser.displayMemberPath = 'header';
        colChooser.isContentHtml = true;
        colChooser.showGroups = true;

        const colChooserPane = document.createElement('div');
        colChooserPane.append(colChooser.hostElement);
        colChooserPane.classList.add('column-picker-pane');
        const hidePopupFunc = () => hidePopup(colChooserPane, true, false);

        // now a menu
        const buttonBar = document.createElement('div');
        buttonBar.classList.add('button-bar');
        colChooserPane.append(buttonBar);

        const btnClose = WjGridColumnChooser.createButton('Close', 'far fa-xmark', hidePopupFunc);
        const btnDefaultColWidths = WjGridColumnChooser.createButton('Reset to default width', 'far fa-text-width', () => {
            try {
                grid.beginUpdate();
                grid.columns.forEach(col => col.width = undefined)
                this.clearMemorizedColumnWidths();
            } finally {
                grid.endUpdate();
            }
        });

        buttonBar.append(btnDefaultColWidths, btnClose);
        colChooser.lostFocus.addHandler(hidePopupFunc);

        // now register the context menu event handler and cleanup
        // any earlier registered event handlers.
        const earlierHandlers = (grid as any)['columnchooser-event-menus'] as RegisteredDomEventHandlers ?? [];
        Object.entries(earlierHandlers).forEach(([event, reg]) => {
            grid.hostElement.removeEventListener(event, reg.handler, reg.options);
        });

        const contextMenuEventHandler = (e: MouseEvent) => {
            e.preventDefault();
            const ht = grid.hitTest(e.pageX, e.pageY);
            switch (ht.cellType) {
                case CellType.ColumnHeader:
                    cv.refresh();
                    showPopup(colChooserPane, e, PopupPosition.BelowRight, false, false);
                    break;

                case CellType.Cell:
                case CellType.RowHeader:
            }
        };
        grid.hostElement.addEventListener('contextmenu', contextMenuEventHandler, true);
        grid.hostElement.addEventListener('click', hidePopupFunc);

        (grid as any)['columnchooser-event-menus'] = {
            contextmenu: { handler: contextMenuEventHandler, options: true },
            click: { contextMenuEventHandler: hidePopupFunc }
        };
    }

    private static createButton(text: string, iconClz: string | undefined, click: (e: MouseEvent) => void) {
        const btn = document.createElement('fluent-button') as Button;
        // btn.textContent = text;
        btn.title = text;
        btn.onclick = click;

        if (iconClz) {
            const icon = document.createElement('i');
            icon.classList.add(...iconClz.split(' '));
            btn.prepend(icon, document.createTextNode(' '));
        }

        return btn;
    }
}
